import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import LiveSessionComponent from '../components/LiveSessionComponent';
import { 
    Send, 
    Plus, 
    MessageSquare, 
    Trash2, 
    Loader2,
    Mic,
    MicOff,
    Volume2,
    Crown,
    ChevronLeft,
    ChevronRight,
    GraduationCap,
    StopCircle,
    AlertTriangle,
    Radio,
    BookOpen,
    ClipboardList,
    Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UstadhPage() {
    const navigate = useNavigate();
    const { user, token, isPremium } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [currentSession, setCurrentSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingSessions, setLoadingSessions] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768); // Closed on mobile by default
    const [isRecording, setIsRecording] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [ttsLoading, setTtsLoading] = useState(false);
    const [showLiveSession, setShowLiveSession] = useState(false);
    const [learningProfile, setLearningProfile] = useState(null);
    const messagesEndRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchLearningProfile = async () => {
        try {
            const response = await axios.get(`${API}/ustadh/profile`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLearningProfile(response.data);
        } catch (error) {
            console.error('Failed to fetch learning profile:', error);
        }
    };

    const fetchChatHistory = async () => {
        try {
            const response = await axios.get(`${API}/chat/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSessions(response.data.filter(s => s.mode === 'ustadh'));
        } catch (error) {
            console.error('Failed to fetch chat history:', error);
        } finally {
            setLoadingSessions(false);
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (!isPremium) {
            navigate('/subscription');
            return;
        }
        fetchChatHistory();
        fetchLearningProfile();
    }, [token, isPremium, navigate]);

    const loadSession = async (sessionId) => {
        try {
            const response = await axios.get(`${API}/chat/session/${sessionId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCurrentSession(response.data);
            setMessages(response.data.messages || []);
        } catch (error) {
            toast.error('Failed to load session');
        }
    };

    const startNewChat = () => {
        // Generate a completely new session to reset LLM state
        // This mimics what happens on login - fresh session, fresh budget tracking
        setCurrentSession(null);
        setMessages([]);
        // Force a refresh of the session list
        fetchChatHistory();
    };

    const deleteSession = async (sessionId, e) => {
        e.stopPropagation();
        try {
            await axios.delete(`${API}/chat/session/${sessionId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSessions(sessions.filter(s => s.session_id !== sessionId));
            if (currentSession?.session_id === sessionId) {
                startNewChat();
            }
            toast.success('Session deleted');
        } catch (error) {
            toast.error('Failed to delete session');
        }
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMessage = {
            role: 'user',
            content: input.trim(),
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        try {
            const response = await axios.post(`${API}/ustadh/chat`, {
                message: userMessage.content,
                session_id: currentSession?.session_id
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const assistantMessage = {
                role: 'assistant',
                content: response.data.response,
                timestamp: new Date().toISOString()
            };

            setMessages(prev => [...prev, assistantMessage]);
            
            if (!currentSession) {
                setCurrentSession({ session_id: response.data.session_id });
                fetchChatHistory();
            }
        } catch (error) {
            toast.error('Failed to send message', {
                description: error.response?.data?.detail || 'Please try again'
            });
            setMessages(prev => prev.slice(0, -1));
        } finally {
            setLoading(false);
        }
    };

    const startRecording = async () => {
        // First check if mediaDevices API is available
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            toast.error('Microphone not supported', {
                description: 'Your browser does not support microphone access. Please use a modern browser.',
                duration: 8000
            });
            return;
        }

        try {
            // Check permission status first
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
                    if (permissionStatus.state === 'denied') {
                        toast.error('Microphone blocked', {
                            description: 'Please click the lock icon in your browser address bar, enable microphone, and refresh.',
                            duration: 10000
                        });
                        return;
                    }
                } catch (permErr) {
                    console.log('Permissions API not fully supported');
                }
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Store stream reference for cleanup
            const streamRef = stream;
            
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };

            mediaRecorderRef.current.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                await transcribeAudio(audioBlob);
                streamRef.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            toast.info('Recording started', { description: 'Recite your Quran portion clearly' });
        } catch (error) {
            console.error('Microphone error:', error);
            
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                toast.error('Microphone permission needed', {
                    description: 'Allow microphone access when prompted. If no prompt, click the lock icon in your address bar.',
                    duration: 10000
                });
            } else if (error.name === 'NotFoundError') {
                toast.error('No microphone found', {
                    description: 'Please connect a microphone to your device.',
                    duration: 8000
                });
            } else {
                toast.error('Microphone error', {
                    description: `Unable to access microphone: ${error.message || 'Unknown error'}`,
                    duration: 8000
                });
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const transcribeAudio = async (audioBlob) => {
        setLoading(true);
        const formData = new FormData();
        formData.append('audio_file', audioBlob, 'recording.webm');

        try {
            const response = await axios.post(`${API}/ustadh/stt`, formData, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            const userMessage = {
                role: 'user',
                content: `[Voice Recitation]\n${response.data.transcribed_text}`,
                timestamp: new Date().toISOString()
            };

            const assistantMessage = {
                role: 'assistant',
                content: response.data.feedback,
                timestamp: new Date().toISOString()
            };

            setMessages(prev => [...prev, userMessage, assistantMessage]);
        } catch (error) {
            toast.error('Failed to transcribe audio', {
                description: error.response?.data?.detail || 'Please try again'
            });
        } finally {
            setLoading(false);
        }
    };

    const speakText = async (text) => {
        setTtsLoading(true);
        try {
            const response = await axios.post(`${API}/ustadh/tts`, {
                text: text.slice(0, 500) // Limit text length
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.mock) {
                toast.info('Voice playback (demo mode)', {
                    description: 'ElevenLabs API key not configured for real voice'
                });
            } else {
                const audio = new Audio(response.data.audio_url);
                audioRef.current = audio;
                
                audio.onplay = () => setIsPlaying(true);
                audio.onended = () => setIsPlaying(false);
                audio.onerror = () => {
                    setIsPlaying(false);
                    toast.error('Audio playback error');
                };
                
                await audio.play();
            }
        } catch (error) {
            toast.error('Failed to generate speech', {
                description: error.response?.data?.detail || 'Please try again'
            });
        } finally {
            setTtsLoading(false);
        }
    };

    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
        }
    };

    const getSessionTitle = (session) => {
        const firstMessage = session.messages?.[0];
        if (firstMessage?.content) {
            return firstMessage.content.slice(0, 30) + (firstMessage.content.length > 30 ? '...' : '');
        }
        return 'New Session';
    };

    return (
        <div className="min-h-screen bg-background">
            <Navbar />
            
            <div className="flex pt-16 h-screen">
                {/* Sidebar - Hidden on mobile by default */}
                <AnimatePresence>
                    {sidebarOpen && (
                        <motion.aside
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 280, opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="border-r border-border bg-card overflow-hidden fixed md:relative z-20 h-[calc(100vh-4rem)] md:h-auto"
                        >
                            <div className="p-4 h-full flex flex-col">
                                <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-secondary/10 border border-secondary/20">
                                    <Crown className="w-5 h-5 text-secondary" />
                                    <span className="font-semibold text-secondary">Ustadh Mode</span>
                                </div>

                                <Button 
                                    onClick={startNewChat}
                                    className="w-full mb-2 bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                                    data-testid="new-session-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    New Chat Session
                                </Button>

                                <Button 
                                    onClick={() => setShowLiveSession(true)}
                                    variant="outline"
                                    className="w-full mb-4 border-primary/50 text-primary hover:bg-primary/10"
                                    data-testid="live-session-btn"
                                >
                                    <Radio className="w-4 h-4 mr-2" />
                                    Start Live Recitation
                                </Button>

                                <div className="text-xs font-medium text-muted-foreground mb-2 px-2">
                                    LEARNING HISTORY
                                </div>

                                <ScrollArea className="flex-1">
                                    {loadingSessions ? (
                                        <div className="flex items-center justify-center py-8">
                                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                                        </div>
                                    ) : sessions.length === 0 ? (
                                        <div className="text-center py-8 text-muted-foreground text-sm">
                                            No sessions yet
                                        </div>
                                    ) : (
                                        <div className="space-y-1">
                                            {sessions.map((session) => (
                                                <div
                                                    key={session.session_id}
                                                    onClick={() => loadSession(session.session_id)}
                                                    className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                                        currentSession?.session_id === session.session_id
                                                            ? 'bg-secondary/10 text-secondary'
                                                            : 'hover:bg-muted text-foreground'
                                                    }`}
                                                    data-testid={`ustadh-session-${session.session_id}`}
                                                >
                                                    <GraduationCap className="w-4 h-4 shrink-0" />
                                                    <span className="flex-1 truncate text-sm">
                                                        {getSessionTitle(session)}
                                                    </span>
                                                    <button
                                                        onClick={(e) => deleteSession(session.session_id, e)}
                                                        className="opacity-0 group-hover:opacity-100 p-1 hover:text-destructive transition-opacity"
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </ScrollArea>
                            </div>
                        </motion.aside>
                    )}
                </AnimatePresence>

                {/* Toggle Sidebar Button */}
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="fixed md:absolute left-0 top-20 md:top-1/2 md:-translate-y-1/2 z-30 p-2 bg-card border border-border rounded-r-lg hover:bg-muted transition-colors shadow-lg"
                    style={{ left: sidebarOpen ? '280px' : '0' }}
                    data-testid="toggle-ustadh-sidebar"
                >
                    {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>

                {/* Mobile overlay when sidebar is open */}
                {sidebarOpen && (
                    <div 
                        className="fixed inset-0 bg-black/50 z-10 md:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Main Chat Area */}
                <main className="flex-1 flex flex-col">
                    {/* Disclaimer Banner */}
                    <div className="bg-secondary/10 border-b border-secondary/20 px-4 py-2">
                        <div className="max-w-3xl mx-auto flex items-center gap-2 text-sm text-secondary">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            <p>
                                <strong>Please note:</strong> Aalim.AI cannot issue fatwas or replace qualified scholars (ulema). 
                                For specific rulings, please consult your local imam or mufti.
                            </p>
                        </div>
                    </div>

                    {/* Messages */}
                    <ScrollArea className="flex-1 p-4">
                        {messages.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center px-4">
                                <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mb-6">
                                    <GraduationCap className="w-8 h-8 text-secondary" />
                                </div>
                                <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                                    Assalamu Alaikum, Student
                                </h2>
                                <p className="text-muted-foreground max-w-md mb-6">
                                    I'm your personal Quran teacher. Practice your recitation, learn tajweed rules,
                                    or ask me about your memorization journey.
                                </p>
                                
                                {/* Live Session CTA */}
                                <div className="w-full max-w-md mb-6">
                                    <Button
                                        onClick={() => setShowLiveSession(true)}
                                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6"
                                        data-testid="start-live-session-cta"
                                    >
                                        <Radio className="w-5 h-5 mr-2" />
                                        <div className="text-left">
                                            <div className="font-semibold">Start Live Recitation Session</div>
                                            <div className="text-xs opacity-80">Practice with real-time feedback & corrections</div>
                                        </div>
                                    </Button>
                                </div>

                                {/* Learning Profile Summary */}
                                {learningProfile && (
                                    <div className="w-full max-w-md mb-6 p-4 rounded-lg bg-muted/30 border border-border">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Sparkles className="w-4 h-4 text-secondary" />
                                            <span className="text-sm font-medium">Your Progress</span>
                                        </div>
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div>
                                                <div className="text-lg font-bold text-primary">
                                                    {learningProfile.progress?.total_sessions || 0}
                                                </div>
                                                <div className="text-xs text-muted-foreground">Sessions</div>
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-secondary">
                                                    {learningProfile.progress?.total_recitation_time || 0}
                                                </div>
                                                <div className="text-xs text-muted-foreground">Minutes</div>
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-yellow-500">
                                                    {learningProfile.level?.overall || 'Beginner'}
                                                </div>
                                                <div className="text-xs text-muted-foreground">Level</div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="text-xs text-muted-foreground mb-3">Or ask me anything:</div>
                                <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                                    {[
                                        'Help me with tajweed rules',
                                        'Create a hifz plan for Juz Amma',
                                        'Teach me about madd rules',
                                        'What\'s my current level?'
                                    ].map((suggestion, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setInput(suggestion)}
                                            className="px-3 py-1.5 text-sm rounded-full border border-secondary/30 hover:border-secondary hover:text-secondary transition-colors"
                                            data-testid={`ustadh-suggestion-${i}`}
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="max-w-3xl mx-auto space-y-6">
                                {messages.map((msg, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                                    >
                                        {msg.role === 'assistant' && (
                                            <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                                                <GraduationCap className="w-4 h-4 text-secondary" />
                                            </div>
                                        )}
                                        <div
                                            className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                                                msg.role === 'user'
                                                    ? 'bg-secondary text-secondary-foreground rounded-tr-none'
                                                    : 'bg-muted rounded-tl-none'
                                            }`}
                                        >
                                            {msg.role === 'assistant' ? (
                                                <div className="text-sm prose prose-sm dark:prose-invert max-w-none">
                                                    <ReactMarkdown
                                                        components={{
                                                            p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                                                            strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                                                            em: ({children}) => <em className="italic">{children}</em>,
                                                            ul: ({children}) => <ul className="list-disc list-inside mb-2">{children}</ul>,
                                                            ol: ({children}) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
                                                            li: ({children}) => <li className="mb-1">{children}</li>,
                                                            h1: ({children}) => <h3 className="font-semibold text-base mb-2">{children}</h3>,
                                                            h2: ({children}) => <h4 className="font-semibold text-base mb-2">{children}</h4>,
                                                            h3: ({children}) => <h5 className="font-semibold text-sm mb-1">{children}</h5>,
                                                            code: ({children}) => <code className="bg-background/50 px-1 py-0.5 rounded text-xs">{children}</code>,
                                                            blockquote: ({children}) => (
                                                                <blockquote className="border-l-2 border-secondary pl-3 italic my-2">
                                                                    {children}
                                                                </blockquote>
                                                            ),
                                                        }}
                                                    >
                                                        {msg.content}
                                                    </ReactMarkdown>
                                                </div>
                                            ) : (
                                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                            )}
                                            {msg.role === 'assistant' && (
                                                <div className="mt-2 pt-2 border-t border-border/50 flex gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => isPlaying ? stopAudio() : speakText(msg.content)}
                                                        disabled={ttsLoading}
                                                        className="h-7 text-xs"
                                                        data-testid={`speak-message-${index}`}
                                                    >
                                                        {ttsLoading ? (
                                                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                                        ) : isPlaying ? (
                                                            <StopCircle className="w-3 h-3 mr-1" />
                                                        ) : (
                                                            <Volume2 className="w-3 h-3 mr-1" />
                                                        )}
                                                        {isPlaying ? 'Stop' : 'Listen'}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                        {msg.role === 'user' && (
                                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                                                <span className="text-xs font-medium">{user?.name?.[0] || 'U'}</span>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                                {loading && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="flex gap-3"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                                            <GraduationCap className="w-4 h-4 text-secondary" />
                                        </div>
                                        <div className="bg-muted rounded-2xl rounded-tl-none px-4 py-3">
                                            <div className="flex gap-1">
                                                <span className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                <span className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                <span className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>
                        )}
                    </ScrollArea>

                    {/* Input Area */}
                    <div className="border-t border-border p-4">
                        <form onSubmit={sendMessage} className="max-w-3xl mx-auto flex gap-2">
                            {/* Voice Recording Button */}
                            <Button
                                type="button"
                                variant={isRecording ? "destructive" : "outline"}
                                onClick={isRecording ? stopRecording : startRecording}
                                disabled={loading}
                                className={isRecording ? 'animate-pulse' : ''}
                                data-testid="voice-record-btn"
                            >
                                {isRecording ? (
                                    <MicOff className="w-4 h-4" />
                                ) : (
                                    <Mic className="w-4 h-4" />
                                )}
                            </Button>
                            
                            <Input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask about tajweed, hifz, or start a lesson..."
                                className="flex-1"
                                disabled={loading || isRecording}
                                data-testid="ustadh-input"
                            />
                            <Button 
                                type="submit" 
                                disabled={!input.trim() || loading || isRecording}
                                className="bg-secondary hover:bg-secondary/90"
                                data-testid="ustadh-send"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" />
                                )}
                            </Button>
                        </form>
                        <p className="text-xs text-muted-foreground text-center mt-2">
                            {isRecording ? (
                                <span className="text-secondary">Recording... Click the mic button to stop and get feedback</span>
                            ) : (
                                'Click the microphone to record your Quran recitation for tajweed feedback'
                            )}
                        </p>
                    </div>
                </main>
            </div>

            {/* Live Session Modal */}
            <AnimatePresence>
                {showLiveSession && (
                    <LiveSessionComponent
                        onClose={() => {
                            setShowLiveSession(false);
                            fetchLearningProfile(); // Refresh profile after session
                        }}
                        onSessionEnd={(summary) => {
                            toast.success('Session completed!', {
                                description: `${summary.duration_minutes} minutes of practice`
                            });
                        }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
